/**
 * Custom hook for currency display and conversion
 * Provides easy-to-use currency conversion for components
 */
import { useCurrency } from '../context/CurrencyContext';

export const useCurrencyDisplay = () => {
  const { currency, rate, loading, convert, formatPrice, isUSD } = useCurrency();

  /**
   * Convert and format a USD price for display
   */
  const displayPrice = (priceUSD) => {
    if (!priceUSD) return formatPrice(0);
    const converted = convert(priceUSD);
    return formatPrice(converted);
  };

  /**
   * Get converted amount without formatting
   */
  const getConvertedAmount = (priceUSD) => {
    if (!priceUSD) return 0;
    return convert(priceUSD);
  };

  /**
   * Format USD equivalent text
   */
  const getUSDEquivalent = (priceUSD) => {
    if (isUSD || !priceUSD) return null;
    return `$${priceUSD.toFixed(2)} USD`;
  };

  /**
   * Get formatted display with both USD and converted currency
   * Returns: "$100.00 USD ≈ ¥15,200 JPY"
   */
  const getFormattedWithBoth = (priceUSD) => {
    if (!priceUSD) return '$0.00 USD';
    if (isUSD) return `$${priceUSD.toFixed(2)} USD`;
    
    const converted = convert(priceUSD);
    const convertedFormatted = formatPrice(converted);
    return `$${priceUSD.toFixed(2)} USD ≈ ${convertedFormatted}`;
  };

  /**
   * Get disclaimer text for non-USD currencies
   */
  const getDisclaimer = () => {
    if (isUSD) return null;
    return `Prices in ${currency} are for reference only. Final charges will be processed in USD.`;
  };

  /**
   * Check if current currency is not USD (local currency)
   * Returns function for backward compatibility
   */
  const isLocalCurrency = () => {
    return !isUSD;
  };

  /**
   * Get prices object with converted amounts
   * For backward compatibility with existing code
   */
  const getPrices = (priceUSD) => {
    const convertedAmount = convert(priceUSD);
    return {
      original: priceUSD,
      converted: convertedAmount,
      formatted: formatPrice(convertedAmount),
      currency: currency,
      isUSD: isUSD
    };
  };

  return {
    currency,
    rate,
    loading,
    isUSD,
    displayPrice,
    getConvertedAmount,
    getUSDEquivalent,
    getFormattedWithBoth,
    getDisclaimer,
    isLocalCurrency,
    getPrices,
    formatPrice
  };
};
